'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { Beer, Building2, ChefHat, Leaf, Refrigerator, Snowflake, Sparkles, Store, Warehouse, Wine, type LucideIcon } from '@/components/homepage-october/lucide'

import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/homepage-october/ui/tabs'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipLinks, ChipRows, Eyebrow, Section, SectionHeader, SubHeader, words } from '../blocks/layout'
import { ProductCard } from '../blocks/ProductCard'
import { ProductRail } from '../blocks/Rail'
import type { FunCard, Model } from '../types'

/* ------------------------------------------------------------------ local helpers */

/** Product shots (cut-outs on white) sit contained; lifestyle photos fill their frame. */
const isCutout = (src: string) => /\/product-images\/|\/tiles\/|\/canvas\/fridge-com-|\/canvas\/equator-|cutout/i.test(src)

/** A soft light glow behind a section's content (decorative, light tints only). */
function Glow({ className }: { className: string }) {
  return <div aria-hidden="true" className={compactClassName(cn('pointer-events-none absolute rounded-full', className))} />
}

// A decorative icon for a photo card, picked from its destination and title.
const CARD_ICON: [RegExp, LucideIcon, string][] = [
  [/ice-makers|\bice\b/i, Snowflake, 'text-[#0891b2]'],
  [/wine/i, Wine, 'text-[#be123c]'],
  [/beer|bar-coolers|kegerator/i, Beer, 'text-[#b54708]'],
  [/display/i, Store, 'text-[#0454c0]'],
  [/commercial|kitchen/i, ChefHat, 'text-[#0454c0]'],
]

const CARD_THEME = {
  warm: 'hover:shadow-[0_28px_60px_-32px_rgba(194,65,12,0.45)] hover:ring-[#ff9951]/45',
  teal: 'hover:shadow-[0_28px_60px_-32px_rgba(4,105,240,0.5)] hover:ring-[#0454c0]/35',
} as const

/** A big lifestyle card: photo on top, h3 title and text below; the whole card is one link (as in v2). */
function PhotoCard({ c, theme }: { c: FunCard; theme: keyof typeof CARD_THEME }) {
  const cut = isCutout(c.img.src)
  const hit = CARD_ICON.find(([re]) => re.test(`${c.href} ${c.title}`))
  const Icon = hit ? hit[1] : Sparkles
  return (
    <DestinationLink
      href={c.href}
      className={cn('group flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-black/5 transition duration-300 motion-safe:hover:-translate-y-1', CARD_THEME[theme])}
    >
      <div className={compactClassName(cn('relative aspect-[4/3] overflow-hidden', cut ? 'bg-white' : 'bg-neutral-100'))}>
        <Img
          src={c.img.src}
          alt={c.img.alt}
          fit={cut ? 'contain' : 'cover'}
          className={cn('absolute inset-0 size-full transition-transform duration-700 motion-safe:group-hover:scale-[1.05]', cut && 'p-[7%]')}
        />
        <span aria-hidden="true" className={compactClassName(cn('absolute top-4 left-4 flex size-10 items-center justify-center rounded-full bg-white/95 shadow-md ring-1 ring-black/5', hit ? hit[2] : 'text-[#0469f0]'))}>
          <Icon className="size-5" />
        </span>
      </div>
      <div className={compactClassName("flex flex-1 flex-col gap-2 p-5 md:p-6")}>
        <h3 className={compactClassName("flex items-center justify-between gap-3 text-xl font-semibold tracking-tight text-neutral-950 md:text-[1.35rem]")}>
          {c.title}
          <ArrowRight aria-hidden="true" className="size-5 shrink-0 text-neutral-400 transition motion-safe:group-hover:translate-x-1 group-hover:text-neutral-900" />
        </h3>
        <p className={compactClassName("text-pretty text-[15px] leading-relaxed text-neutral-600")}>{c.text}</p>
      </div>
    </DestinationLink>
  )
}

/* ------------------------------------------------------------------ V12 specialty: tabs, chips, kegerator spotlight */

const TAB_ICON: Record<string, LucideIcon> = { wine: Wine, mini: Refrigerator, ice: Snowflake, com: Building2, keg: Beer }
const TAB_TINT: Record<string, string> = {
  wine: 'text-[#be123c]',
  mini: 'text-[#0469f0]',
  ice: 'text-[#0891b2]',
  com: 'text-[#0454c0]',
  keg: 'text-[#b54708]',
}

function Spotlight({ s }: { s: Model['specialty']['spot'] }) {
  return (
    <Card className="mt-8 gap-0! overflow-hidden border-[#ff9951]/30! bg-[linear-gradient(135deg,#fff7f0_0%,#ffffff_55%,#fff1e6_100%)]! py-0! shadow-[0_30px_70px_-42px_rgba(180,83,9,0.5)]!">
      <div className={compactClassName("grid gap-8 p-5 sm:p-7 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)] xl:items-center xl:gap-8")}>
        <div>
          <Eyebrow tone="sand">{s.eyebrow}</Eyebrow>
          <h3 className={compactClassName("mt-3 text-balance text-2xl font-bold tracking-tight text-neutral-950 md:text-3xl")}>{s.title}</h3>
          <p className={compactClassName("mt-3 text-pretty text-sm leading-relaxed text-neutral-600 md:text-base")}>{s.text}</p>
          <ChipLinks links={s.chips} className="mt-5" />
          <Button asChild variant="ctaPrimary" className="mt-6 h-11! rounded-full! px-6! font-semibold!">
            <DestinationLink href={s.cta.href} aria-label={s.cta.aria}>
              {words(s.cta.text)}
              <ArrowRight aria-hidden="true" />
            </DestinationLink>
          </Button>
        </div>
        <ul className={compactClassName("grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4")}>
          {s.products.map((p) => (
            <li key={p.fin}>
              <ProductCard p={p} headingLevel={4} />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  )
}

export function Specialty({ model }: { model: Model }) {
  const m = model.specialty
  return (
    <Section id={m.id} aria={m.aria} v2="V12" tone="lilac" className="overflow-hidden">
      <Glow className="-top-44 -left-40 size-[560px] bg-[radial-gradient(circle,rgba(4,105,240,0.13),transparent_65%)]" />
      <Glow className="top-[30%] -right-52 size-[520px] bg-[radial-gradient(circle,rgba(255,153,81,0.14),transparent_65%)]" />
      <div className={compactClassName("relative")}>
        <SectionHeader head={{ title: m.title }} tone="lilac" />
        {/* The selected panel is server-rendered; Radix mounts other complete product rails when selected. */}
        <Tabs defaultValue={m.tabs[0]?.key} className="gap-0!">
          <TabsList aria-label={m.tabsAria} className="h-auto! w-full! flex-wrap justify-start! gap-2! rounded-none! bg-transparent! p-0!">
            {m.tabs.map((t) => {
              const I = TAB_ICON[t.key] ?? Sparkles
              return (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  className="h-10! flex-none! gap-2! rounded-full! border-neutral-200! bg-white! px-4! text-sm font-semibold! text-neutral-700! shadow-xs! data-[state=inactive]:hover:border-[#0469f0]/45! data-[state=inactive]:hover:text-[#0454c0]! data-[state=active]:border-[#0469f0]! data-[state=active]:bg-[#eef5ff]! data-[state=active]:text-[#0454c0]! data-[state=active]:shadow-[0_10px_22px_-12px_rgba(4,105,240,0.6)]!"
                >
                  <I aria-hidden="true" className={cn('size-4!', TAB_TINT[t.key] ?? 'text-[#0469f0]')} />
                  {t.label}
                </TabsTrigger>
              )
            })}
          </TabsList>
          {m.tabs.map((t) => (
            <TabsContent
              key={t.key}
              value={t.key}
              data-motion
              data-live={t.live ? '' : undefined}
              className="mt-8 data-[state=active]:animate-v3-rise data-[state=inactive]:hidden"
            >
              <ProductRail products={t.products} title={t.label} sub={t.sub} viewAll={t.viewAll} />
            </TabsContent>
          ))}
        </Tabs>
        <ChipRows rows={m.chiprows} className="mt-8!" />
        <Spotlight s={m.spot} />
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V12b fun moments + under-the-counter rails */

export function Fun({ model }: { model: Model }) {
  const m = model.fun
  return (
    <Section aria={m.aria} v2="V12b" tone="peach">
      <SectionHeader head={{ eyebrow: m.eyebrow, title: m.title }} tone="peach" />
      <ul className={compactClassName("grid gap-5 md:grid-cols-3 md:gap-6")}>
        {m.cards.map((c) => (
          <li key={c.href}>
            <PhotoCard c={c} theme="warm" />
          </li>
        ))}
      </ul>
      <div className={compactClassName("mt-10 grid gap-10")}>
        {m.rails.map((r) => (
          <ProductRail key={r.title} products={r.products} title={r.title} sub={r.sub} viewAll={r.viewAll} id={r.id} level={2} />
        ))}
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V12c garage, apartment-size, ENERGY STAR */

// Each rail sits on its own light band with an icon cue; picked by the rail title, then by position.
const RAIL_LOOK: { re: RegExp; Icon: LucideIcon; band: string; chip: string }[] = [
  { re: /garage/i, Icon: Warehouse, band: 'bg-[#f3f8ff] ring-[#0469f0]/10', chip: 'text-[#0454c0]' },
  { re: /apartment/i, Icon: Building2, band: 'bg-[#fff7f0] ring-[#ff9951]/30', chip: 'text-[#b54708]' },
  { re: /energy/i, Icon: Leaf, band: 'bg-[#f0faf4] ring-[#10a65c]/15', chip: 'text-[#067647]' },
]

function ThemedRail({ rail: r }: { rail: Model['rails']['rails'][number] }) {
  const look = RAIL_LOOK.find((x) => x.re.test(r.title)) ?? RAIL_LOOK[0]
  const I = look.Icon
  return (
    <div id={r.wrapId} className={compactClassName(cn('scroll-mt-28 rounded-3xl p-4 ring-1 sm:p-5 md:p-6', look.band))}>
      <ProductRail
        products={r.products}
        title={r.title}
        sub={r.sub}
        viewAll={r.viewAll}
        id={r.id}
        level={2}
        kicker={
          <span aria-hidden="true" className={compactClassName(cn('v3-rail-icon mb-3 inline-flex size-10 items-center justify-center rounded-xl bg-white shadow-xs ring-1 ring-black/5', look.chip))}>
            <I className="size-5" />
          </span>
        }
      />
    </div>
  )
}

export function ApartmentRail({ model }: { model: Model }) {
  const rail = model.rails.rails.find((r) => /apartment/i.test(r.title))
  if (!rail) return null
  return <Section aria={rail.title} v2="apartment-rail" tone="white"><ThemedRail rail={rail} /></Section>
}

export function GarageRail({ model }: { model: Model }) {
  const rail = model.rails.rails.find((r) => /garage/i.test(r.title))
  return rail ? <ThemedRail rail={rail} /> : null
}

export function Rails({ model }: { model: Model }) {
  const m = model.rails
  return (
    <Section aria={m.aria} v2="V12c" tone="white">
      <div className={compactClassName("grid gap-6 md:gap-8")}>
        {m.rails.filter((r) => !/garage|apartment/i.test(r.title)).map((r) => <ThemedRail key={r.title} rail={r} />)}
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V13 commercial */

export function Commercial({ model }: { model: Model }) {
  const m = model.commercial
  return (
    <Section aria={m.aria} v2="V13" tone="mist" className="overflow-hidden">
      <Glow className="-top-40 right-[-10%] size-[540px] bg-[radial-gradient(circle,rgba(20,184,166,0.12),transparent_65%)]" />
      <div className={compactClassName("relative")}>
        {/* Section heading with a teal accent (same layout as SectionHeader). */}
        <div className={compactClassName("mb-6 max-w-3xl md:mb-8")}>
          <p className={compactClassName("inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#0454c0]")}>
            <span aria-hidden="true" className={compactClassName("size-1.5 rounded-full bg-[#0469f0]")} />
            {m.eyebrow}
          </p>
          <h2 className={compactClassName("mt-3 text-balance text-3xl font-bold tracking-tight text-neutral-950 md:text-[2.5rem] md:leading-[1.1]")}>{m.title}</h2>
        </div>
        <ul className={compactClassName("grid gap-5 md:grid-cols-3 md:gap-6")}>
          {m.cards.map((c) => (
            <li key={c.href}>
              <PhotoCard c={c} theme="teal" />
            </li>
          ))}
        </ul>
        <ChipRows rows={m.chiprows} className="mt-8!" />
        <div className={compactClassName("mt-10 overflow-hidden rounded-3xl bg-white shadow-xs ring-1 ring-black/5")}>
          <div aria-hidden="true" className={compactClassName("h-1.5 bg-gradient-to-r from-[#0469f0] to-[#ff9951]")} />
          <div className={compactClassName("p-4 sm:p-6 md:p-8")}>
            <SubHeader title={m.rowTitle} link={m.rowLink} />
            <ul className={compactClassName("grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4")}>
              {m.products.map((p) => (
                <li key={p.fin}>
                  <ProductCard p={p} headingLevel={4} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>
  )
}
